import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Type, Palette, Download, ArrowRight } from 'lucide-react';

const HowItWorksSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #0f172a 0%, #1a1637 100%);
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 10rem 0;
  }
`;

const HowItWorksContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 10;

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;

  @media (min-width: 768px) {
    margin-bottom: 6rem;
  }
`;

const HowItWorksTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  letter-spacing: -0.025em;
  color: white;

  span {
    background: linear-gradient(45deg, #7c3aed, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (min-width: 640px) {
    font-size: 3rem;
  }

  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (min-width: 768px) {
    font-size: 1.375rem;
  }
`;

const StepsContainer = styled.div`
  position: relative;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 4rem;
  }
`;

const ConnectingLine = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: block;
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 15%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 85%
    );
    z-index: 1;
    transform: translateY(-50%);
  }
`;

const StepCard = styled.div`
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0.12) 100%
  );
  backdrop-filter: blur(15px);
  padding: 3rem 2rem;
  border-radius: 2rem;
  position: relative;
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.2),
    inset 0 1px 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.4s ease;
  text-align: center;
  z-index: 2;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible }) =>
    isVisible ? 'translateY(0)' : 'translateY(30px)'};
  transition-delay: ${({ delay }) => delay || '0ms'};
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 3.5rem 2.5rem;
  }
`;

const LargeBackgroundIcon = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.1);
  z-index: 1;
  pointer-events: none;

  svg {
    width: 120px;
    height: 120px;
    stroke-width: 1;
    opacity: 0.3;
  }
`;

const LargeBackgroundNumber = styled.div`
  position: absolute;
  top: -0.5rem;
  right: -2.5rem;
  font-size: 14rem;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.121);
  line-height: 1;
  user-select: none;
  z-index: 1;
  opacity: 0.6;
`;

const StepContent = styled.div`
  position: relative;
  z-index: 10;
  text-align: center;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const StepDescription = styled.p`
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
  font-size: 1rem;
  max-width: 90%;
  margin: 0 auto;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ArrowConnector = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    right: -1rem;
    transform: translateY(-50%);
    z-index: 3;
    color: rgba(255, 255, 255, 0.6);

    &:last-child {
      display: none;
    }
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: 5rem;
  padding-top: 3rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const CTAButton = styled.button`
  background: linear-gradient(45deg, #7c3aed, #ec4899);
  color: white;
  font-weight: 600;
  font-size: 1.125rem;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 25px rgba(124, 58, 237, 0.3);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const steps = [
  {
    number: '1',
    icon: <Type />,
    title: 'Enter Your Content',
    description:
      'Paste your URL, add text, or upload data. Our smart detector handles the rest automatically.',
  },
  {
    number: '2',
    icon: <Palette />,
    title: 'Customize Design',
    description:
      'Choose colors, add your logo, and select from professional templates to match your brand.',
  },
  {
    number: '3',
    icon: <Download />,
    title: 'Download & Share',
    description:
      'Get your QR code in high quality PNG, SVG, or PDF format. Ready for digital and print use.',
  },
];

export default function HowItWorks() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <HowItWorksSection id="how" ref={sectionRef}>
      <HowItWorksContainer>
        <SectionHeader>
          <HowItWorksTitle>
            How It <span>Works</span>
          </HowItWorksTitle>
          <SectionSubtitle>
            Create professional QR codes in three simple steps. No technical
            knowledge required.
          </SectionSubtitle>
        </SectionHeader>

        <StepsContainer>
          <ConnectingLine />

          <StepsGrid>
            {steps.map((step, index) => (
              <div key={index} style={{ position: 'relative' }}>
                <StepCard isVisible={isVisible} delay={`${index * 200}ms`}>
                  <LargeBackgroundIcon>{step.icon}</LargeBackgroundIcon>

                  <LargeBackgroundNumber>{step.number}</LargeBackgroundNumber>

                  <StepContent>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                  </StepContent>
                </StepCard>

                {index < steps.length - 1 && (
                  <ArrowConnector>
                    <ArrowRight size={24} />
                  </ArrowConnector>
                )}
              </div>
            ))}
          </StepsGrid>
        </StepsContainer>

        <CTASection>
          <CTAButton>
            Try It Now
            <ArrowRight size={18} />
          </CTAButton>
        </CTASection>
      </HowItWorksContainer>
    </HowItWorksSection>
  );
}
